// Sequencer Connect - the tags panel shared by both platforms' parameter steps.
//
// This is the wizard's one sanctioned fetching component: GET /:id/tags is a
// read-only proxy over the customer's workspace - it lists tags and never
// creates one, so browsing it leaves nothing behind in their account - and the
// list is only wanted while a params step is on screen. Putting it in the shell
// would mean calling the sequencer on every step the operator visits.
//
// It is SHARED because the tags contract is platform-independent: the
// standardized PI_YYYY_MM_DD tag is created (or reused by exact name) at Start
// and only at Start, picked ids are re-validated against the live list then,
// and new names typed here are created then too. Only the endpoint behind the
// proxy differs per platform, and the server owns that branch.

import { useEffect, useState } from 'react';

import { sqApi, sqErrorMessage, type SqTag } from '../sequencerApi';

export interface TagsPanelProps {
  runId: string;
  /** The run's standardized PI_YYYY_MM_DD tag, stamped at creation. */
  tagName: string;
  existingIds: string[];
  setExistingIds: (ids: string[]) => void;
  newNames: string[];
  setNewNames: (names: string[]) => void;
  busy: boolean;
}

const NEW_TAG_INPUT_ID = 'sq-new-tag';

export function TagsPanel({
  runId,
  tagName,
  existingIds,
  setExistingIds,
  newNames,
  setNewNames,
  busy,
}: TagsPanelProps): JSX.Element {
  const [tags, setTags] = useState<SqTag[] | null>(null);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [newTag, setNewTag] = useState('');

  // Loaded once per mount. A failure here is NOT fatal to the step: the
  // standardized tag is created by Start regardless, and the extra-tag picker is
  // the only thing that goes missing.
  useEffect(() => {
    let live = true;
    setTagsLoading(true);
    sqApi
      .listTags(runId)
      .then(({ tags: fresh }) => {
        if (!live) return;
        setTags(fresh);
        setTagsError(null);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setTags(null);
        setTagsError(sqErrorMessage(err));
      })
      .finally(() => {
        if (live) setTagsLoading(false);
      });
    return () => {
      live = false;
    };
  }, [runId]);

  function addNewTag(): void {
    const name = newTag.trim();
    if (name === '') return;
    const clash =
      newNames.some((n) => n.toLowerCase() === name.toLowerCase()) ||
      name.toLowerCase() === tagName.toLowerCase();
    if (clash) {
      setNewTag('');
      return;
    }
    // A name that already exists in the workspace is picked rather than typed:
    // Start finds-or-creates by exact name, so both paths end at the same tag,
    // but showing it as a tick keeps the list honest about what is new.
    const existing = (tags ?? []).find((t) => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!existingIds.includes(existing.id)) setExistingIds([...existingIds, existing.id]);
    } else {
      setNewNames([...newNames, name]);
    }
    setNewTag('');
  }

  return (
    <>
      <div className="sq-notice sq-notice--info">
        Tag <strong>{tagName}</strong> will be auto-created in this workspace and applied to
        every connected account. If it already exists it is reused - never duplicated.
      </div>

      <div className="sq-field">
        <span className="sq-field-label">Extra tags (optional)</span>
        {tagsLoading && <span className="sq-hint">Loading the workspace's tags...</span>}
        {tagsError && (
          <div className="sq-notice sq-notice--warn">
            The workspace's existing tags could not be listed: {tagsError} You can still type new
            tag names below, and {tagName} is applied either way.
          </div>
        )}
        {tags !== null && tags.length === 0 && (
          <span className="sq-hint">This workspace has no tags yet.</span>
        )}
        {tags !== null && tags.length > 0 && (
          <div className="sq-checks">
            {tags.map((t) => (
              <label key={t.id} className="sq-check">
                <input
                  type="checkbox"
                  checked={existingIds.includes(t.id)}
                  onChange={(e) =>
                    setExistingIds(
                      e.target.checked
                        ? [...existingIds, t.id]
                        : existingIds.filter((id) => id !== t.id),
                    )
                  }
                  disabled={busy}
                />
                <span>{t.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="sq-field">
        <label className="sq-field-label" htmlFor={NEW_TAG_INPUT_ID}>
          Add a new tag
        </label>
        <div className="sq-actions">
          <input
            id={NEW_TAG_INPUT_ID}
            className="sq-input"
            type="text"
            value={newTag}
            maxLength={200}
            placeholder="Tag name"
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Otherwise Enter in a text input submits the surrounding form
                // in some browsers, which here means leaving the step.
                e.preventDefault();
                addNewTag();
              }
            }}
            disabled={busy}
          />
          <button
            type="button"
            className="sq-btn sq-btn--secondary"
            onClick={() => addNewTag()}
            disabled={busy || newTag.trim() === ''}
          >
            Add
          </button>
        </div>
        {newNames.length > 0 && (
          <div className="sq-chips">
            {newNames.map((n) => (
              <span key={n} className="sq-chip">
                {n}
                <button
                  type="button"
                  className="sq-chip-x"
                  onClick={() => setNewNames(newNames.filter((x) => x !== n))}
                  disabled={busy}
                  aria-label={`Remove ${n}`}
                >
                  x
                </button>
              </span>
            ))}
          </div>
        )}
        <span className="sq-hint">
          New tags are created in the customer's workspace when the run starts, not now. A name
          that already exists there is ticked in the list above instead of created twice.
        </span>
      </div>
    </>
  );
}
